import * as ents from '@attain-apps/entities'
import * as store from '@attain-apps/fe/web/main/lib/store'
import { TypeChecker } from '@attain-apps/type-guards'
import * as rxjs from 'rxjs'

import type { Plugin } from '../plugin'
import type { Context } from '../types'

/**
 * Calls api to apply functional usage updates for a space cluster
 */
export const applyUpdatesOnRequest = store.effects.create(
  (feature: Plugin['feature'], plugin: Plugin) =>
    feature.actions.applyUpdates.request.observe().pipe(
      rxjs.withLatestFrom(
        plugin.collection.feature.selectors.context.asObservable,
        plugin.collection.plugins.currentEntityPlugin.feature.selectors.getId()
          .asObservable,
        feature.selectors.editingFunctionalUsage.asObservable,
      ),
      rxjs.mergeMap(
        ([action, collectionContext, clusterId, editingFunctionalUsage]) => {
          if (TypeChecker.isNullish(collectionContext)) {
            return rxjs.of(
              feature.actions.applyUpdates.cancellation.from(action).create({
                reason: 'No collection context available',
                request: action.data,
              }),
            )
          }
          if (!ents.extra.space.Cluster.Id.isId(clusterId)) {
            return rxjs.of(
              feature.actions.applyUpdates.cancellation.from(action).create({
                reason: 'No cluster ID available',
                request: action.data,
              }),
            )
          }
          if (TypeChecker.isNullish(editingFunctionalUsage)) {
            return rxjs.of(
              feature.actions.applyUpdates.cancellation.from(action).create({
                reason: 'No editing functional usage available',
                request: action.data,
              }),
            )
          }

          const context: Context = {
            ...collectionContext,
            clusterId,
          }

          return plugin.api
            .update({ context, functionalizations: editingFunctionalUsage })
            .pipe(
              rxjs.map(response =>
                feature.actions.applyUpdates.success.from(action).create({
                  request: action.data,
                  response,
                }),
              ),
              rxjs.catchError((error: Error) =>
                rxjs.of(
                  feature.actions.applyUpdates.error.from(action).create({
                    error,
                    request: action.data,
                  }),
                ),
              ),
            )
        },
      ),
    ),
)
